// Дзвіночок у топбарі (D26, ТЗ 4.2.14.1): лічильник непрочитаних (опитування раз на 60 с), випадний список останніх 20,
// «Позначити все прочитаним», перехід за посиланням з позначкою прочитаного. Разом із ним монтується контроль
// бездіяльності сеансу (ТЗ 4.1.5.3) — один елемент у маркерах U16 топбару.
import { DEFAULT_SESSION_IDLE_TIMEOUT_MIN } from '@unisched/shared';
import { Bell, BellOff, CheckCheck } from 'lucide-react';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { isApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatInt } from '@/lib/format';
import { Badge, Button, EmptyState, IconButton, Popover, Skeleton } from '@/ui';
import { useMarkAllRead, useMarkRead, useNotifications, usePolicy, useUnreadCount, type NotificationItem } from './api';
import { NotificationTypeBadge, safeLink, timeAgo, UnreadDot } from './notification-view';
import { SessionIdleGuard } from './SessionIdleGuard';

const RECENT = { page: 1, pageSize: 20 } as const;

function BellPanel({ close, unread }: { close: () => void; unread: number }) {
  const list = useNotifications(RECENT);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const navigate = useNavigate();

  const open = (n: NotificationItem) => {
    if (!n.readAt) markRead.mutate(n.id);
    const to = safeLink(n.link);
    close();
    if (to) void navigate(to);
  };

  return (
    <div className="flex w-[min(calc(100vw-4.5rem),380px)] flex-col">
      <div className="flex items-center justify-between gap-2 py-1 pr-1 pl-2.5">
        <span className="flex items-center gap-2 text-[14px] font-semibold">
          Сповіщення
          {unread ? (
            <Badge size="sm" tone="primary">
              {formatInt(unread)} нових
            </Badge>
          ) : null}
        </span>
        <Button size="sm" variant="ghost" icon={CheckCheck} disabled={!unread} loading={markAll.isPending} onClick={() => markAll.mutate()} aria-label="Позначити все прочитаним" title="Позначити все прочитаним">
          <span className="max-sm:hidden">Позначити все прочитаним</span>
        </Button>
      </div>
      <div className="max-h-[min(52vh,420px)] overflow-y-auto scroll-thin" role="list">
        {list.isLoading ? (
          <div className="flex flex-col gap-2 p-2.5">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : list.error ? (
          <EmptyState compact tone="danger" icon={BellOff} title="Не вдалося завантажити сповіщення" action={<Button size="sm" onClick={() => void list.refetch()}>Спробувати ще раз</Button>} />
        ) : !list.data?.items.length ? (
          <EmptyState compact icon={Bell} title="Сповіщень немає" description="Тут з'являться повідомлення про публікацію розкладу, зміни й погодження." />
        ) : (
          list.data.items.map((n) => (
            <button
              key={n.id}
              type="button"
              role="listitem"
              onClick={() => open(n)}
              className={cn('flex w-full gap-2.5 rounded-[8px] px-2.5 py-2 text-left hover:bg-surface-2', !n.readAt && 'bg-primary-soft/40')}
            >
              <UnreadDot unread={!n.readAt} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <NotificationTypeBadge type={n.type} />
                  <span className="num shrink-0 text-[11px] text-faint">{timeAgo(n.createdAt)}</span>
                </span>
                <span className={cn('mt-1 block text-[13px] leading-5', !n.readAt && 'font-semibold')}>{n.title}</span>
                {n.body ? <span className="line-clamp-2 block text-[12px] leading-4 whitespace-pre-line text-muted">{n.body}</span> : null}
              </span>
            </button>
          ))
        )}
      </div>
      <div className="border-t border-divider p-1.5">
        <Link to="/notifications" onClick={close} className="block rounded-[8px] px-2.5 py-1.5 text-center text-[13px] font-semibold text-primary hover:bg-surface-2">
          Усі сповіщення
        </Link>
      </div>
    </div>
  );
}

export interface NotificationsBellProps {
  /** Показати замість дзвіночка, якщо API сповіщень недоступне (сервер без модуля U16 — 404). */
  fallback?: ReactNode;
}

/** Дзвіночок сповіщень + контроль бездіяльності сеансу (елемент топбару в маркерах U16). */
export function NotificationsBell({ fallback }: NotificationsBellProps) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const policy = usePolicy();
  const unread = useUnreadCount(policy.data?.notificationPollSec ?? 60);
  if (unread.error && isApiError(unread.error) && unread.error.isNotFound && fallback) return <>{fallback}</>;
  const count = unread.data?.count ?? 0;
  return (
    <>
      {/* налаштування недоступні — типовий тайм-аут, а не вимкнений контроль; поки завантажуються — очікування */}
      <SessionIdleGuard timeoutMin={policy.data?.sessionIdleTimeoutMin ?? (policy.isError ? DEFAULT_SESSION_IDLE_TIMEOUT_MIN : null)} />
      <IconButton
        ref={anchor}
        icon={Bell}
        badge={count}
        aria-expanded={open}
        aria-haspopup="dialog"
        label={count ? `Сповіщення: ${formatInt(count)} непрочитаних` : 'Сповіщення'}
        onClick={() => setOpen((v) => !v)}
      />
      <Popover open={open} onClose={close} anchorRef={anchor} align="end" maxHeight={640} role="dialog" aria-label="Сповіщення">
        <BellPanel close={close} unread={count} />
      </Popover>
    </>
  );
}
